import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import CardContext from 'contexts/CardContext';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import useExplainValue from 'components/InsightsChat/explainValue/useExplainValue';
import useGadgetDrillTarget from 'components/InsightsChat/explainValue/useGadgetDrillTarget';
import useGetMetricIdsFromVis from 'hooks/useGetMetricIdsFromVis';

import FilterPlatesContext from '../../contexts/FilterPlatesContext';
import buildSyncKeywordChart from './buildSyncKeywordChart';
import buildSyncDateChart from './buildSyncDateChart';
import buildOnDateDrillDown from './buildOnDateDrillDown';
import buildOnKeywordDrillDown from './buildOnKeywordDrillDown';
import buildOnRangeDrillDown from './buildOnRangeDrillDown';
import getIsHistogram from './getIsHistogram';
import getIsRangeChart from './getIsRangeChart';
import DashboardGadgetContext from '../../contexts/DashboardGadgetContext';
import usePopupCallback from '../usePopupCallback';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import { HighchartsReactRefObject } from 'highcharts-react-official';
import useDateScope from '../useDateScope';
import { MaybeExplain } from './buildOnKeywordDrillDown';

const useChartDrillDowns = (
  field: string,
  chartRef?: React.MutableRefObject<HighchartsReactRefObject | null>,
  chartType?: GadgetType | 'SingleMetricDateMatrix' | 'PaceMatrix',
  chartDef?: V5ChartDefinition,
  autoInterval?: AutoInterval,
) => {
  const { setDrillDowns, drillDownsAsFixed } = useContext(FilterPlatesContext);
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const [isLastDrillForField, setIsLastDrillForField] =
    useState<boolean>(false);
  const maybeOpenPopupReport = usePopupCallback(dashboardGadget);

  const isHistogram = getIsHistogram(chartDef);
  const isRangeChart = getIsRangeChart(chartDef);
  const dateScope = useDateScope({});

  // Insights "pick a value to explain". While armed, the next click is handed
  // to the chat as a focus instead of filtering the report or opening a popup;
  // undefined otherwise, so the unarmed path through the builders is untouched.
  // The card's own definition stands in when the caller passes no V5 chartDef
  // (ranking/pace/single-metric-date matrices).
  const { explainPicker } = useContext(InsightsChatContext);
  const { isAvailable: isExplainAvailable, explain } = useExplainValue();
  const { i: cardId, chartDefinition } = useContext(CardContext);
  const getMetricIds = useGetMetricIdsFromVis();
  const drillTarget = useGadgetDrillTarget(dashboardGadget);
  const isPickerArmed = explainPicker.isArmed && isExplainAvailable;
  const maybeExplain = useMemo((): MaybeExplain | undefined => {
    if (!isPickerArmed) {
      return undefined;
    }
    const definition = chartDef ?? chartDefinition;
    return ({ drillDown, term }) =>
      explain({
        drillDown,
        term,
        metricIds: getMetricIds(definition),
        chartDef: definition,
        cardId,
        // Both view builders title a gadget by its chart def name.
        gadgetTitle: definition?.name ?? '',
        drillTarget,
        source: 'picker',
      });
  }, [
    isPickerArmed,
    chartDef,
    chartDefinition,
    explain,
    getMetricIds,
    cardId,
    drillTarget,
  ]);

  useEffect(() => {
    const lastDrill = drillDownsAsFixed[drillDownsAsFixed.length - 1];
    if (autoInterval) {
      const shouldSync =
        drillDownsAsFixed.length > 0 &&
        lastDrill.field === autoInterval.interval;
      setIsLastDrillForField(shouldSync);
    } else {
      const shouldSync =
        drillDownsAsFixed.length > 0 &&
        lastDrill.field === field &&
        !lastDrill.isExclude;
      setIsLastDrillForField(shouldSync);
    }
  }, [autoInterval, field, drillDownsAsFixed]);

  const track = useCallback(() => {
    trackEvent('Report - Card - Filtering Interaction');
  }, [trackEvent]);

  const syncKeywordChart = useCallback(
    (drillDown?: FixedFilter) =>
      buildSyncKeywordChart({
        isLastDrillForField,
        drillDowns: drillDownsAsFixed,
        isRangeChart,
        chartDef,
        chartRef,
        chartType,
      })(drillDown),
    [
      isLastDrillForField,
      drillDownsAsFixed,
      isRangeChart,
      chartDef,
      chartRef,
      chartType,
    ],
  );

  const interval = autoInterval
    ? autoInterval.interval
    : chartDef && chartDef.trendByCalendarInterval;

  const syncDateChart = useCallback(
    (drillDown?: FixedFilter) =>
      buildSyncDateChart({
        chartType,
        isLastDrillForField,
        chartRef,
        drillDowns: drillDownsAsFixed,
        interval,
      })(drillDown),
    [chartType, isLastDrillForField, chartRef, drillDownsAsFixed, interval],
  );

  const onDateDrillDown = useCallback(
    (term: string, ctrlKey?: boolean) =>
      buildOnDateDrillDown({
        chartDef,
        drillDowns: drillDownsAsFixed,
        field,
        setDrillDowns,
        isLastDrillForField,
        autoInterval,
        maybeOpenPopupReport,
        maybeExplain,
        track,
        dateScope,
      })(term, ctrlKey),
    [
      chartDef,
      dateScope,
      drillDownsAsFixed,
      field,
      setDrillDowns,
      isLastDrillForField,
      autoInterval,
      maybeOpenPopupReport,
      maybeExplain,
      track,
    ],
  );

  const onKeywordDrillDown = useCallback(
    (term: string, ctrlKey?: boolean) =>
      buildOnKeywordDrillDown({
        chartDef,
        drillDowns: drillDownsAsFixed,
        field,
        setDrillDowns,
        isLastDrillForField,
        maybeOpenPopupReport,
        maybeExplain,
        track,
      })(term, ctrlKey),
    [
      chartDef,
      drillDownsAsFixed,
      field,
      setDrillDowns,
      isLastDrillForField,
      maybeOpenPopupReport,
      maybeExplain,
      track,
    ],
  );

  const onRangeDrillDown = useCallback(
    (term: string) =>
      buildOnRangeDrillDown({
        chartDef,
        drillDowns: drillDownsAsFixed,
        field,
        setDrillDowns,
        isLastDrillForField,
        maybeOpenPopupReport,
        maybeExplain,
        track,
      })(term),
    [
      chartDef,
      drillDownsAsFixed,
      field,
      setDrillDowns,
      isLastDrillForField,
      maybeOpenPopupReport,
      maybeExplain,
      track,
    ],
  );

  if (!chartDef) {
    return {
      onDrillDown: onKeywordDrillDown,
      syncChart: syncKeywordChart,
      isLastDrillForField,
    };
  }

  const onDrillDown = (() => {
    if (isRangeChart) {
      return onRangeDrillDown;
    }

    if (isHistogram) {
      return onDateDrillDown;
    }

    return onKeywordDrillDown;
  })();

  const syncChart = (() => {
    if (isRangeChart) {
      return syncKeywordChart;
    }

    if (isHistogram) {
      return syncDateChart;
    }

    return syncKeywordChart;
  })();

  return {
    onDrillDown,
    syncChart,
    isLastDrillForField,
  };
};

export default useChartDrillDowns;
